import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { PortfolioSummary as PortfolioSummaryType } from '../types';
import { formatINR } from '../utils/currency';

const PortfolioSummary: React.FC = () => {
  const [summary, setSummary] = useState<PortfolioSummaryType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getPortfolioSummary();
      setSummary(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load portfolio summary');
      console.error('Error fetching portfolio summary:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-6 animate-pulse">
            <div className="h-4 bg-white/10 rounded w-1/2 mb-4"></div>
            <div className="h-8 bg-white/10 rounded w-3/4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const totalInvested = parseFloat(summary.total_invested);
  const currentValue = parseFloat(summary.current_value);
  const totalRealizedGain = parseFloat(summary.total_realized_gain);
  const totalUnrealizedGain = parseFloat(summary.total_unrealized_gain);
  const totalGain = parseFloat(summary.total_gain);
  const returnPercentage = parseFloat(summary.return_percentage);

  const stats = [
    {
      label: 'Current Value',
      value: formatINR(currentValue),
      subValue: `Invested: ${formatINR(totalInvested)}`,
      icon: '💰',
      color: 'from-blue-500/20 to-cyan-500/20',
    },
    {
      label: 'Total Gain/Loss',
      value: formatINR(totalGain),
      subValue: `${returnPercentage >= 0 ? '+' : ''}${returnPercentage.toFixed(2)}%`,
      icon: totalGain >= 0 ? '📈' : '📉',
      color: totalGain >= 0 ? 'from-green-500/20 to-emerald-500/20' : 'from-red-500/20 to-pink-500/20',
      valueColor: totalGain >= 0 ? 'text-green-400' : 'text-red-400',
    },
    {
      label: 'Unrealized Gain/Loss',
      value: formatINR(totalUnrealizedGain),
      subValue: 'Current holdings',
      icon: '📊',
      color: totalUnrealizedGain >= 0 ? 'from-green-500/20 to-emerald-500/20' : 'from-red-500/20 to-pink-500/20',
      valueColor: totalUnrealizedGain >= 0 ? 'text-green-400' : 'text-red-400',
    },
    {
      label: 'Realized Gain/Loss',
      value: formatINR(totalRealizedGain),
      subValue: 'From sales',
      icon: '💵',
      color: totalRealizedGain >= 0 ? 'from-green-500/20 to-emerald-500/20' : 'from-red-500/20 to-pink-500/20',
      valueColor: totalRealizedGain >= 0 ? 'text-green-400' : 'text-red-400',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`rounded-xl border border-white/10 bg-gradient-to-br ${stat.color} p-6 transition-all hover:border-white/20`}
          >
            <div className="flex items-start justify-between mb-4">
              <p className="text-sm font-medium text-slate-400">{stat.label}</p>
              <span className="text-2xl">{stat.icon}</span>
            </div>
            <p className={`text-2xl font-bold mb-1 ${stat.valueColor || 'text-white'}`}>
              {stat.value}
            </p>
            <p className="text-sm text-slate-500">{stat.subValue}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PortfolioSummary;